import {
  Button,
  Collapse,
  Divider,
  Paper,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  useMemo, useState, useEffect
} from "react";

/* eslint-disable react-hooks/exhaustive-deps */
import uuid from "react-uuid";
import { useTraitsAndMetrics } from "../../../../../hooks/QueryHooks";
import MultiSelectDropdown from "../../../../../shared/ui/dropdown/MultiSelectDropdown";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { getUserPinnedTraits, updateUserPinnedTraits } from "../../../../../store/preference/preferencesHelpers";
import usePreferencesStore from "../../../../../store/preference/preferencesStore";
import { PencilIcon } from "lucide-react";
import TruncatedTextTooltip from "../../../../../shared/ui/truncate/TruncatedText";

type Props = {
  contact: any,
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
};

const UserTraits = ({ contact, size = 'sm' }: Props) => {
  const [showTraitDropdownOpen, setShowTraitDropdownOpen] = useState(false);
  const [showAllTraits, { toggle: toggleShowAllTraits }] = useDisclosure(false);

  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const preferences = usePreferencesStore((state) => state.preferences);
  const [displayedTraits, setDisplayedTraits] = useState<string[]>([]);
  
  useEffect(() => {
    const userTraits = getUserPinnedTraits();
    if (userTraits && userTraits.length > 0) {
      setDisplayedTraits(userTraits);
    }
  }, [preferences]);

  const onSelectTrait = async (trait: any) => {
    const newTraits = displayedTraits.includes(trait.name)
      ? displayedTraits.filter((name: any) => name !== trait.name)
      : [...displayedTraits, trait.name];
    
    const previousTraits = [...displayedTraits];
    setDisplayedTraits(newTraits);
    
    try {
      await updateUserPinnedTraits(newTraits);
    } catch (error) {
      console.error('Failed to update user traits:', error);
      setDisplayedTraits(previousTraits);
    }
  };

  const { data }: any = useTraitsAndMetrics({
    workspaceId,
    enabled: !!workspaceId,
  });

  const shownTraits = useMemo(() => Object
    .entries(contact || {}).reduce((obj: any, [trKey, trValue]: any) => {
      if (displayedTraits?.some((item: any) => item === `users.${trKey}`)) {
        const selectedTrait = data?.contactTraits?.obj[`users.${trKey}`];
        if (selectedTrait && !selectedTrait.exclude_display) {
          obj[trKey] = {
            ...selectedTrait,
            val: trValue,
          };
        }
      }
      return obj;
    }, {}), [
    displayedTraits,
    data?.contactTraits?.obj, contact]);

  return (
    <Paper shadow="none" p="0" radius="md" style={{ position: "relative" }}>
      <Divider
        label={
          <div className="flex items-center gap-2">
            User Traits
            <MultiSelectDropdown
              selected={displayedTraits}
              position="bottom-start"
              setSelected={onSelectTrait}
              isOpen={showTraitDropdownOpen}
              setIsOpen={setShowTraitDropdownOpen}
              list={data?.contactTraits?.list?.filter((trait: any) => !trait.exclude_display) || []}
            >
              <PencilIcon className="cursor-pointer w-3 h-3" />
            </MultiSelectDropdown>
          </div>
        }
        labelPosition="center"
      />
      <div>
        {(Object.entries(shownTraits).slice(0, 5)).map(([, value]: [string, any]) => (
          <div key={uuid()} className="flex justify-between items-center py-2 border-b border-dashed border-gray-100 last:border-b-0">
            <Text size={size} c="gray.6">{value.display_name}</Text>
            <TruncatedTextTooltip copy width={150} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
          </div>
        ))}
        <Collapse in={showAllTraits}>
          {(Object.entries(shownTraits).slice(5)).map(([, value]: [string, any]) => (
            <div key={uuid()} className="flex justify-between items-center py-2 border-b border-dashed border-gray-100 last:border-b-0">
              <Text size={size} c="gray.6">{value.display_name}</Text>
              <TruncatedTextTooltip copy width={150} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
            </div>
          ))}
        </Collapse>
        {Object.entries(shownTraits).length > 5 && (
          <div className="flex justify-center mt-2">
            <Button 
              variant="subtle" 
              size="xs" 
              onClick={toggleShowAllTraits}
            >
              {showAllTraits ? 'See less' : `See more (${Object.entries(shownTraits).length - 5})`}
            </Button>
          </div>
        )}
      </div>
    </Paper>
  );
};

export default UserTraits;
